/**
 * planet_look — planet shader v2, phases 1, 2, 4 and 5 (report §9 R4).
 *
 * The planets were `MeshStandardMaterial`s lit by whatever the scene held (a 1.3 ambient + a hemisphere that
 * flattened every terminator; in an alt system the ONE sector light pointed from its star at the SHIP, so a
 * planet across the star was lit from the wrong side) under an additive rim that glowed all the way round —
 * night side included. This material lights itself from ITS OWN sun direction (a per-planet uniform — sys0's sun
 * at infinity, an alt system's star at the origin), so every terminator faces the star that lights it:
 *  1. COHERENT LIGHTING — Lambert with a terminator WRAP scaled by the atmosphere's thickness (air carries light
 *     a little past the geometric terminator; an airless moon cuts hard), the normal map's detail faded out across
 *     the terminator (Sangil Lee: bumps never light the night side), a starlight floor, no ambient.
 *  2. OCEAN GLINT — a GGX lobe (α ≈ 0.12²) with Schlick Fresnel on the albedo's blue, dark texels (the ocean
 *     mask): the moving sun glint, the strongest "real planet" cue.
 *  4. NIGHT LIGHTS — warm city light fading on past the terminator, smoothstep(−0.05, −0.25, N·L); for the Commons
 *     the clusters are the real settlement sites baked into a small equirect (`cityLightsTexture`).
 *  5. IN-SCATTER OVER THE DISC — a view-grazing veil in the planet's OWN rim hue (not the constant 0.58), thicker
 *     toward the limb and only on the day side; the O'Neil shell still draws the limb beyond the disc (med+).
 * The texture nodes start on 1×1 placeholders and take the loaded maps by `.value` (no rebuild).
 *
 * SPACE LIGHT (shader gaps R4, 2026-09-30): with `light` the planet is a RECEIVER — the limb-darkened eclipse by the
 * bodies its set picks and its own ring's shadow (one visibility on the direct sun, the glint and the veil), and on the
 * Commons the orbital cloud shell's shadow (space_light_look.ts; the pure mirrors in eclipse / rings / orbital_clouds).
 *
 * NATURAL OBJECTS (2026-10-07): with `surface` (natural/world_look.PlanetSurface) the same material draws a KIND of world
 * over a pooled face — a cloud deck that hides the ground, drowned continents, latitude bands and a great storm, an ice
 * shell's lineae, molten cracks that glow hottest under the star, a giant's thermal glow, auroral ovals, a haze-soft limb.
 * Every term is arithmetic on the body's own direction (no branch, no second texture); fine patterns go to their mean as
 * they shrink under a pixel. Absent = the material above, node for node (the Commons Reach's planets).
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float, uv, texture, normalView, positionViewDirection, cameraViewMatrix, dot, max, pow,
  smoothstep, clamp, normalize, mix, normalMap, positionWorld, modelWorldMatrix,
  modelWorldMatrixInverse, positionGeometry, dFdx, dFdy, fwidth, sqrt,
  mx_noise_float, time, abs, min, exp, sin, acos, length, step, cameraPosition,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { createPlanetAtmosphereMaterial } from 'enginex/render/sky/effects/PlanetAtmosphereMaterial.js';
import { createPlanetAtmosphereScatterMaterial } from 'enginex/render/sky/effects/PlanetAtmosphereScatterMaterial.js';
// shader gaps R4 (space light): the receiver-side eclipse, the ring's shadow and the orbital cloud shadow (space_light_look.ts)
import { planetFrame, tangentRel, dirUvN, seatBodyLayer, wrapShellVisibility, type EclipseSet, type OrbitalCloudBaker } from './space_light_look.js';
import { CITY_MAP, CLOUD_SHELL_K, CLOUD_SHADOW_K } from './orbital_clouds.js';
import { ATLAS_ATMOSPHERE } from '../sky/atmosphere.js';
import type { PlanetSurface, ShellExtra } from './natural/world_look.js'; // natural objects: a KIND's surface terms (pure)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export const planetLookState = { materials: 0, cityTexels: 0 };

/** The sun colour × intensity every planet shares (linear rgb; SpaceModule drives it with the key light). */
export const uPlanetSun = uniform(new THREE.Color(3.2, 3.0, 2.8));

let placeholderAlbedo: THREE.DataTexture | null = null;
let placeholderNormal: THREE.DataTexture | null = null;
let placeholderBlack: THREE.DataTexture | null = null;
const tex1 = (r: number, g: number, b: number, srgb: boolean): THREE.DataTexture => {
  const t = new THREE.DataTexture(new Uint8Array([r, g, b, 255]), 1, 1, THREE.RGBAFormat);
  if (srgb) t.colorSpace = THREE.SRGBColorSpace;
  t.needsUpdate = true;
  t.userData.shared = true;
  return t;
};

export interface PlanetLookOpts {
  /** The planet's atmosphere / rim colour (sRGB hex) — the in-scatter hue. */
  rim: number;
  /** Atmosphere thickness 0 (airless) … ~1.4 (a gas giant) — wraps the terminator and weights the in-scatter. */
  atmosphere: number;
  /** An ocean world (the glint lobe). */
  ocean: boolean;
  /** An albedo tint multiplier (alt systems' pooled faces), sRGB hex. */
  tint?: number;
  /** The city-light equirect (null = none). */
  cities?: THREE.Texture | null;
  /** The unit direction TO the sun, world frame. */
  sunDir: THREE.Vector3;
  /**
   * shader gaps R4 — SPACE LIGHT: the planet as a RECEIVER. ONE visibility (the eclipse by the bodies its set picks ×
   * its own ring's shadow) multiplies the direct sun, the ocean glint and the in-scatter veil; the cloud shell's
   * shadow darkens the direct sun and the glint (the veil is above the clouds). Absent = the old look, bit for bit.
   */
  light?: PlanetLight | null;
  /** natural objects — the planet KIND's own surface over the pooled face (world_look.ts). Absent = the old look, bit for bit. */
  surface?: PlanetSurface | null;
}
export interface PlanetLight {
  /** The planet's own eclipse frame (true metres about its centre). */
  set: EclipseSet;
  /** The planet radius (m). */
  R: number;
  /** Its ring casts a shadow on it. */
  ring?: boolean;
  /** Its orbital cloud shell (the Commons). */
  clouds?: OrbitalCloudBaker | null;
}

export interface PlanetLook {
  material: THREE.Material;
  /** Set the loaded albedo / normal map (the placeholders go). */
  setMap(t: THREE.Texture): void;
  setNormal(t: THREE.Texture, bump: number): void;
  /** THE FALLBACK (natural objects, 2026-10-08): the catalog face this look was built for did not load and the POOLED face is
   *  shown instead — measure it against the pooled face's own mean again (`surface.flatten.poolMean`). A no-op otherwise. */
  wearPooled(): void;
  /** Install the night-side city lights later (the Commons' sites are computed after the planets build). */
  setCities(t: THREE.Texture): void;
  /** The normal-map strength (the distance fade drives it). */
  bump: { value: number };
  sunDir: { value: THREE.Vector3 };
  hasNormal: boolean;
}

// ── natural objects: a planet KIND's surface over a pooled face (the pure spec: natural/world_look.PlanetSurface) ──
const hexV = (hex: number): N => { const c = new THREE.Color(hex); return vec3(c.r, c.g, c.b); };
const seedV = (seed: number, a: number, b: number, c: number): N => vec3((seed * a) % 97, (seed * b) % 89, (seed * c) % 83);
const LUMA = (): N => vec3(0.2126, 0.7152, 0.0722);
interface SurfacePre { albedo: N; dirO: N; foot: N; lum0: N }
/**
 * The albedo: the face pulled, drowned, banded, stained and lined. Called INSIDE the material's Fn. `dirO` is the unit
 * direction in the body's own frame (the mesh spins about y, so every pattern turns with the face); `foot` one pixel's
 * size on that unit sphere — a derivative, so it is pinned here, at the top (THE LAZY-VAR LAW).
 */
function surfaceAlbedo(sf: PlanetSurface, tex: N, albedo0: N, faceMean: N): SurfacePre {
  const dirO = normalize(positionGeometry).toVar();
  const foot = length(fwidth(dirO)).toVar();
  const lum0 = dot(tex.rgb, LUMA()).toVar();
  let alb: N = albedo0;
  if (sf.drown) {
    // no continents: everything that is not cloud (bright AND colourless) goes under one ocean — the face's land and
    // its pale shelf seas too, which would still draw every coast
    const t = tex.rgb;
    const mx = max(t.r, max(t.g, t.b)), mn = min(t.r, min(t.g, t.b));
    const sat = mx.sub(mn).div(max(mx, float(0.02)));
    const cloud = smoothstep(0.26, 0.5, lum0).mul(float(1).sub(smoothstep(0.3, 0.62, sat)));
    alb = mix(hexV(sf.drown.sea).mul(lum0.mul(0.3).add(0.9)), alb, cloud);
  }
  if (sf.flatten) {
    const f = sf.flatten;
    const detail = clamp(lum0.div(faceMean).sub(1), -1, 1.2).mul(f.keep).add(1); // (a uniform: the worn face's mean, the pooled face's after a fallback)
    alb = mix(alb, hexV(f.color).mul(max(detail, float(0))), f.amount);
  }
  if (sf.blotch) {
    const b = sf.blotch;
    const n = mx_noise_float(dirO.mul(b.scale).add(seedV(b.seed, 0.37, 0.61, 0.83)));
    alb = mix(alb, hexV(b.color).mul(clamp(lum0.mul(1.4).add(0.55), 0.4, 1.3)), smoothstep(-0.05, 0.42, n).mul(b.amount));
  }
  if (sf.bands) {
    const b = sf.bands;
    const wob = mx_noise_float(dirO.mul(vec3(1.6, 5.0, 1.6)).add(seedV(b.seed, 0.29, 0.11, 0.73))).mul(b.warp);
    const y = dirO.y.add(wob);
    const wave = sin(y.mul(b.count * 1.5708).add(sin(y.mul(b.count * 0.61).add(b.seed * 0.7)).mul(1.4)).add(b.seed * 1.3));
    // band-limited and mean-preserving: as a band shrinks toward a pixel the pattern goes to its own average
    const keep = float(1).sub(smoothstep(0.12, 0.45, foot.mul(b.count)));
    const t = mix(float(0.5), smoothstep(-0.55, 0.55, wave), keep);
    alb = alb.mul(mix(vec3(1, 1, 1), mix(hexV(b.dark), hexV(b.light), t), b.contrast));
  }
  if (sf.storm) {
    const st = sf.storm, cl = Math.cos(st.lat);
    const c = new THREE.Vector3(cl * Math.cos(st.lon), Math.sin(st.lat), cl * Math.sin(st.lon));
    const east = new THREE.Vector3(-Math.sin(st.lon), 0, Math.cos(st.lon)), north = new THREE.Vector3().crossVectors(east, c);
    const e = dirO.sub(vec3(c.x, c.y, c.z));
    const d = length(vec2(dot(e, vec3(east.x, east.y, east.z)).div(st.size * 1.7), dot(e, vec3(north.x, north.y, north.z)).div(st.size)));
    const m = float(1).sub(smoothstep(0.55, 1.0, d)).mul(step(0, dot(dirO, vec3(c.x, c.y, c.z))));
    alb = mix(alb, hexV(st.color).mul(clamp(lum0.mul(0.9).add(0.62), 0.5, 1.2)), m.mul(st.amount));
  }
  if (sf.lines) {
    const l = sf.lines, w = l.width;
    const p = dirO.mul(l.scale);
    const r1 = float(1).sub(abs(mx_noise_float(p.add(seedV(l.seed, 0.13, 0.71, 0.29)))));
    const r2 = float(1).sub(abs(mx_noise_float(p.mul(1.9).add(seedV(l.seed, 0.53, 0.17, 0.91)))));
    const net = max(smoothstep(1 - w, 1 - w * 0.35, r1), smoothstep(1 - w * 0.8, 1 - w * 0.3, r2).mul(0.7));
    // unresolved lines become their mean cover (never a shimmer)
    let cover: N = mix(net, float(Math.min(0.6, w * 2.2)), smoothstep(0.3, 1.2, foot.mul(l.scale / w)));
    if (l.polar > -1) cover = cover.mul(float(1).sub(smoothstep(l.polar - 0.12, l.polar + 0.12, dirO.y)));
    alb = mix(alb, hexV(l.color), cover.mul(l.amount));
  }
  if (sf.cap) {
    const cp = sf.cap;
    const lat: N = cp.north && cp.south ? abs(dirO.y) : cp.north ? dirO.y : dirO.y.negate();
    alb = mix(alb, hexV(cp.color), smoothstep(cp.lat - cp.soft, cp.lat + cp.soft, lat));
  }
  return { albedo: min(alb, vec3(1, 1, 1)).toVar(), dirO, foot, lum0 };
}
/** What the kind adds to the lit picture: the limb's haze, an ice sheen, and everything that glows by itself. */
function surfaceLight(sf: PlanetSurface, pre: SurfacePre, x: { Ng: N; L: N; V: N; ndlG: N; albedo: N; shade: N; spec: N; sunWorld: N }): { limbLit: N; limbW: N; gloss: N; emissive: N } {
  const { dirO, foot, lum0 } = pre;
  let limbLit: N = vec3(0, 0, 0), limbW: N = float(0), gloss: N = vec3(0, 0, 0), emissive: N = vec3(0, 0, 0);
  if (sf.limb) {
    limbW = pow(float(1).sub(clamp(dot(x.Ng, x.V), 0, 1)), float(sf.limb.power)).mul(sf.limb.amount);
    limbLit = hexV(sf.limb.color).mul(x.shade);
  }
  if (sf.gloss) {
    const a = sf.gloss.roughness * sf.gloss.roughness, a2 = float(a * a);
    const H = normalize(x.L.add(x.V));
    const ndh = max(dot(x.Ng, H), float(0));
    const den = ndh.mul(ndh).mul(a2.sub(1)).add(1);
    const D = a2.div(den.mul(den).mul(Math.PI));
    const F = float(0.04).add(float(0.96).mul(pow(float(1).sub(max(dot(x.V, H), float(0))), float(5))));
    const ice = smoothstep(0.25, 0.6, dot(x.albedo, LUMA()));
    gloss = x.spec.mul(D.mul(F).mul(max(x.ndlG, float(0))).mul(0.25).min(3)).mul(ice).mul(sf.gloss.strength);
  }
  if (sf.lava) {
    const lv = sf.lava, w = lv.width;
    const p = dirO.mul(lv.scale);
    const sd = seedV(lv.seed, 0.19, 0.67, 0.41);
    // the crust is broken into plates and the cracks between them glow: a coarse net of wide rifts, a finer one of fissures
    const r1 = float(1).sub(abs(mx_noise_float(p.add(sd))));
    const r2 = float(1).sub(abs(mx_noise_float(p.mul(2.3).add(seedV(lv.seed, 0.59, 0.23, 0.79)))));
    const rift = smoothstep(1 - w, 1 - w * 0.3, r1);
    const fissure = smoothstep(1 - w * 0.7, 1 - w * 0.2, r2).mul(0.55);
    // each net goes to its own mean cover as its lines shrink under a pixel (never a shimmer)
    const riftL = mix(rift, float(Math.min(0.5, w * 1.3)), smoothstep(0.35, 1.3, foot.mul(lv.scale / w)));
    const fisL = mix(fissure, float(Math.min(0.3, w * 0.6)), smoothstep(0.35, 1.3, foot.mul((lv.scale * 2.3) / (w * 0.7))));
    const crack: N = w > 0 ? max(riftL, fisL) : float(0);   // (width 0 = an unbroken crust)
    const heat = pow(max(x.ndlG, float(0)), float(0.75)).mul(1 - lv.night).add(lv.night);   // hottest under the star
    // the magma ocean: round the sub-stellar point the crust has melted through (55 Cancri e); a ragged shore, and
    // rafts of cooling crust adrift on it
    const sea = smoothstep(1 - lv.seas, 1 - lv.seas + 0.14, x.ndlG.add(mx_noise_float(p.mul(0.7).add(sd.zxy)).mul(0.16)));
    const raft = smoothstep(0.02, 0.45, mx_noise_float(p.mul(1.7).add(sd.yzx))).mul(0.8);
    const melt: N = lv.seas > 0 ? sea.mul(float(1).sub(raft)) : float(0);   // (a volcanic moon has hot spots, no ocean)
    const glowing = crack.mul(heat);
    // the ocean convects: bright upwellings in a darker, cooling skin (a flat sheet of one orange reads as paint)
    const cell = mx_noise_float(p.mul(4.3).add(sd.yxz)).mul(0.5).add(0.5);
    // hot spots (a volcanic moon's calderas): a few bright blobs, which go to their mean cover under a pixel
    const thr = 0.74 - 0.3 * (lv.spots ?? 0);
    const spot: N = lv.spots ? mix(smoothstep(thr, thr + 0.07, mx_noise_float(p.mul(1.9).add(sd.zyx))), float(0.03 * lv.spots), smoothstep(0.35, 1.3, foot.mul(lv.scale * 9))).mul(heat) : float(0);
    const hotness = clamp(max(max(melt.mul(cell.mul(0.8).add(0.2)), glowing.mul(heat)), spot), 0, 1);
    emissive = emissive.add(mix(hexV(lv.cool), hexV(lv.hot), hotness).mul(max(max(glowing, melt.mul(cell.mul(0.55).add(0.45))), spot)).mul(lv.gain));
  }
  if (sf.glow) {
    const g = sf.glow;
    const detail = clamp(lum0.mul(1.6).add(0.35), 0.5, 1.5);   // the belts glow unevenly
    if (g.day) {
      // the hot spot rides `shift` east of the sub-stellar point (the winds carry the heat): the sun turned about the spin axis
      const cs = Math.cos(g.day.shift), sn = Math.sin(g.day.shift);
      const sw = x.sunWorld;
      const Ls = normalize(cameraViewMatrix.mul(vec4(sw.x.mul(cs).add(sw.z.mul(sn)), sw.y, sw.z.mul(cs).sub(sw.x.mul(sn)), 0)).xyz);
      emissive = emissive.add(hexV(g.day.color).mul(pow(max(dot(x.Ng, Ls), float(0)), float(g.day.power))).mul(detail).mul(g.day.gain));
    }
    if (g.night) emissive = emissive.add(hexV(g.night.color).mul(float(1).sub(smoothstep(-0.35, 0.55, x.ndlG))).mul(detail).mul(g.night.gain));
    if (g.all) emissive = emissive.add(hexV(g.all.color).mul(detail).mul(g.all.gain));
  }
  if (sf.aurora) {
    const a = sf.aurora, st = Math.sin(a.tilt);
    const m = vec3(st * Math.cos(a.lon), Math.cos(a.tilt), st * Math.sin(a.lon));     // the magnetic axis, tilted off the spin axis
    const col = acos(clamp(abs(dot(dirO, m)), 0, 1));
    const wEff = max(float(a.width), foot.mul(1.3));                                  // an oval thinner than a pixel keeps its light
    const q = col.sub(a.colat).div(wEff);
    const ring = exp(q.mul(q).negate()).mul(float(a.width).div(wEff));
    const curtain = mx_noise_float(dirO.mul(16).add(vec3(time.mul(0.04), a.seed % 53, time.mul(-0.025)))).mul(0.5).add(0.5);
    const nightW = mix(float(1), float(0.3), smoothstep(-0.15, 0.25, x.ndlG));        // the day side outshines them
    emissive = emissive.add(mix(hexV(a.color), hexV(a.color2), curtain).mul(ring.mul(curtain.mul(0.7).add(0.3))).mul(nightW).mul(a.gain));
  }
  return { limbLit, limbW, gloss, emissive };
}

export function planetMaterial(o: PlanetLookOpts): PlanetLook {
  placeholderAlbedo ??= tex1(106, 116, 136, true);
  placeholderNormal ??= tex1(128, 128, 255, false);
  placeholderBlack ??= tex1(0, 0, 0, false);
  // The sampled NODES themselves take the loaded textures by `.value` (the graph never rebuilds).
  const tMap = texture(placeholderAlbedo, uv());
  const tNrm = texture(placeholderNormal, uv());
  const tCity = texture(o.cities ?? placeholderBlack, uv());
  const uSun = uniform(o.sunDir.clone().normalize());
  const uBump = uniform(0);
  const uWrap = uniform(Math.min(0.35, Math.max(0, o.atmosphere) * 0.22));
  const uAtmo = uniform(Math.max(0, o.atmosphere));
  const tintC = new THREE.Color(o.tint ?? 0xffffff);
  const uTint = uniform(tintC);
  const rimC = new THREE.Color(o.rim);
  const uRim = uniform(rimC);
  const uGlint = uniform(o.ocean ? 1 : 0);
  const uCity = uniform(o.cities ? 1 : 0);
  const light = o.light ?? null;
  const sf = o.surface ?? null;
  const uFaceMean = uniform(sf?.flatten?.mean ?? 1); // THE MEAN TRAVELS WITH THE FACE — and comes back with the pooled one (wearPooled)
  const mat = new THREE.MeshBasicNodeMaterial();
  mat.colorNode = Fn(() => {
    // shader gaps R4 — the receiver's visibility and the cloud shadow. Every derivative here sits at the TOP of the
    // shader (THE LAZY-VAR LAW): the ring lookup's footprint and the cloud lookup's gradients (the surface uv's own —
    // continuous across the seam, so the exit point's atan2 seam never picks a blurred mip).
    let vis: N = float(1);
    let cloudT: N = float(1);
    if (light) {
      const fr = planetFrame();
      // .toVar(): a TSL node is EMITTED where it is first used — un-pinned, this fwidth landed inside ringT's `valid`
      // branch (read from the generated GLSL, 2026-10-07): a derivative in non-uniform control flow is undefined, and the
      // ring shadow's edge pixels on the planet took a different footprint from one frame to the next (THE LAZY-VAR LAW).
      const footRho = light.ring ? fwidth(light.set.ringRhoAt(fr.rel)).toVar() : float(0);
      vis = light.set.visAt(fr.rel, { ring: !!light.ring, footRho });
      if (light.clouds) {
        const Lw = light.set.sunAt(fr.rel).L;
        const Lobj = normalize(modelWorldMatrixInverse.mul(vec4(Lw, 0)).xyz);
        const P = positionGeometry;
        const Rc = light.R * CLOUD_SHELL_K;
        const b = dot(P, Lobj);
        const tExit = b.negate().add(sqrt(max(b.mul(b).sub(dot(P, P)).add(Rc * Rc), float(0))));
        const dirC = normalize(P.add(Lobj.mul(tExit)));
        const c = light.clouds.sampleNode(dirUvN(dirC), dFdx(uv()), dFdy(uv()));
        cloudT = float(1).sub(light.clouds.maskNode(c, dirC).mul(CLOUD_SHADOW_K));
      }
    }
    const albedo0 = tMap.rgb.mul(uTint);
    // natural objects: the kind's albedo terms (its direction, pixel footprint and face luminance pinned at the top)
    const pre = sf ? surfaceAlbedo(sf, tMap, albedo0, uFaceMean) : null;
    const albedo = pre ? pre.albedo : albedo0;
    const Ng = normalize(normalView);
    // The perturbed normal (view space; derivative TBN — the sphere carries no tangents).
    const Nm = normalize(normalMap(tNrm, vec2(uBump, uBump)));
    const L = normalize(cameraViewMatrix.mul(vec4(uSun, 0)).xyz);
    const V = positionViewDirection;
    const ndlG = dot(Ng, L);
    // 1. The terminator: bumps fade across it; the air wraps it.
    const ndl = mix(ndlG, dot(Nm, L), smoothstep(-0.05, 0.35, ndlG));
    const diffuse = clamp(ndl.add(uWrap).div(float(1).add(uWrap)), 0, 1);
    const lit = albedo.mul(vec3(uPlanetSun)).mul(diffuse).mul(vis).mul(cloudT);
    // 2. The ocean glint (GGX α = 0.12², Schlick F0 0.02) on blue, dark texels.
    const lum = dot(albedo, vec3(0.2126, 0.7152, 0.0722));
    const ocean = clamp(albedo.b.sub(max(albedo.r, albedo.g)).mul(8), 0, 1).mul(float(1).sub(smoothstep(0.12, 0.35, lum))).mul(uGlint);
    const H = normalize(L.add(V));
    const ndh = max(dot(Ng, H), float(0));
    const a2 = float(0.0144 * 0.0144);
    const den = ndh.mul(ndh).mul(a2.sub(1)).add(1);
    const D = a2.div(den.mul(den).mul(Math.PI));
    const F = float(0.02).add(float(0.98).mul(pow(float(1).sub(max(dot(V, H), float(0))), float(5))));
    const glint = vec3(uPlanetSun).mul(D.mul(F).mul(max(ndlG, float(0))).mul(0.25).min(40)).mul(ocean).mul(vis).mul(cloudT);
    // 4. The night side's city lights.
    const night = smoothstep(-0.05, -0.25, ndlG);
    const cities = tCity.r.mul(1.5).mul(night).mul(uCity); // stored /1.5 in 8 bits
    const lights = vec3(1.0, 0.72, 0.42).mul(cities).mul(2.4);
    // 5. The in-scatter veil in the planet's own hue: grazing view, day side, weighted by the air.
    const fres = pow(float(1).sub(clamp(dot(Ng, V), 0, 1)), float(2.2));
    const day = smoothstep(-0.25, 0.35, ndlG);
    const veil = vec3(uRim).mul(fres.mul(0.9).add(0.05)).mul(day).mul(uAtmo).mul(0.35).mul(vec3(uPlanetSun).x.mul(0.3)).mul(vis);
    if (!sf || !pre) return lit.add(glint).add(lights).add(veil).add(albedo.mul(0.004));
    // natural objects: the kind's light — a haze-soft limb, an ice sheen, what glows by itself, a dim starless sun
    const k = surfaceLight(sf, pre, { Ng, L, V, ndlG, albedo, shade: vec3(uPlanetSun).mul(diffuse).mul(vis).mul(cloudT), spec: vec3(uPlanetSun).mul(vis).mul(cloudT), sunWorld: uSun });
    const sunK = sf.sun ?? 1;
    return mix(lit, k.limbLit, k.limbW).add(glint).add(k.gloss).add(veil).mul(sunK).add(lights).add(k.emissive).add(albedo.mul(0.004));
  })();
  mat.name = 'space-planet-v2';
  planetLookState.materials++;
  const look: PlanetLook = {
    material: mat,
    setMap: (t) => { tMap.value = t; },
    setNormal: (t, bump) => { tNrm.value = t; (uBump as unknown as { value: number }).value = bump; look.hasNormal = true; },
    wearPooled: () => { if (sf?.flatten) (uFaceMean as unknown as { value: number }).value = sf.flatten.poolMean ?? sf.flatten.mean; },
    setCities: (t) => { tCity.value = t; (uCity as unknown as { value: number }).value = 1; },
    bump: uBump as unknown as { value: number },
    sunDir: uSun as unknown as { value: THREE.Vector3 },
    hasNormal: false,
  };
  return look;
}

/**
 * The planet's additive rim shell, SUN-AWARE: it glows on the day limb and fades out on the night side (the old
 * rim ringed the whole disc). `opacity` stays the material property the distance fade drives.
 */
export function planetRimMaterial(rim: number, sunDir: { value: THREE.Vector3 }, opacity: number, light?: PlanetLight | null): THREE.Material {
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, opacity, side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false });
  const uRim = uniform(new THREE.Color(rim));
  mat.colorNode = Fn(() => {
    const centre = modelWorldMatrix.mul(vec4(0, 0, 0, 1)).xyz;
    const nOut = normalize(positionWorld.sub(centre));
    const side = smoothstep(-0.35, 0.45, dot(nOut, sunDir as N));
    // shader gaps R4: the day limb glows only where the sun reaches it (the receiver's visibility at the limb point)
    const vis: N = light ? light.set.visAt(tangentRel(light.R), { ring: !!light.ring }) : float(1);
    return vec3(uRim).mul(side.mul(0.9).add(0.04)).mul(vis);
  })();
  mat.name = 'space-planet-rim';
  markMrtOverlay(mat);
  return seatBodyLayer(mat, 'behind'); // THE BODY LAYER LAW: a back-face shell never asks the depth buffer about its own planet
}

/**
 * The plain additive rim (the charted worlds' and the unlit sector's): the same back-face shell, one colour all round.
 * A NODE material (it was a classic MeshBasicMaterial) so it can stand on its body layer; `opacity` stays the property
 * the distance fade drives.
 */
export function legacyRimMaterial(color: THREE.ColorRepresentation, opacity = 0.16): THREE.Material {
  const mat = new THREE.MeshBasicNodeMaterial({ color, transparent: true, opacity, side: THREE.BackSide, blending: THREE.AdditiveBlending, depthWrite: false });
  mat.name = 'space-planet-rim-plain';
  markMrtOverlay(mat);
  return seatBodyLayer(mat, 'behind');
}

/**
 * A planet's atmosphere shell (SpaceModule.attachAtmosphere's body, shared with the bed): the HOME world's physical
 * scatter shell (space P3, §7.1 — the Commons wears the sky its ground is made of) or O'Neil's tint fit, a back-face
 * additive sphere at 1.025 R, its sun term under the receiver's visibility, an MRT overlay — seated BEHIND its planet
 * (THE BODY LAYER LAW: at the far-body anchor the depth buffer cannot tell the shell's back faces from the planet's
 * front, and an O'Neil chord that leaks through the disc saturates white).
 */
export function buildAtmosphereShell(o: {
  radius: number; hue: number; sunDir: THREE.Vector3; homeSky?: boolean; set?: EclipseSet | null;
} & ShellExtra): { mesh: THREE.Mesh; kind: 'scatter' | 'oneil' | 'haze' } {
  if (o.homeSky) {
    try {
      const { material, uniforms } = createPlanetAtmosphereScatterMaterial({ atmosphere: ATLAS_ATMOSPHERE as never, innerRadius: o.radius, thickness: 1.025, sunDirection: o.sunDir, illuminance: vec3(uPlanetSun as never).mul(Math.PI) });
      if (o.set) wrapShellVisibility(material, o.set, o.radius); // shader gaps R4: ONE visibility on the atmosphere's sun term (at the limb point)
      // sky/post (2026-09-27): an additive depthWrite:false shell — THE MRT OVERLAY LAW (never replace the planet's velocity / normals behind it).
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(o.radius * 1.025, 96, 64), seatBodyLayer(markMrtOverlay(material), 'behind'));
      mesh.name = 'planet-atmosphere-scatter';
      mesh.renderOrder = 2;
      uniforms.sunDirection.value.copy(o.sunDir); // the one sun of the key light, the disc and the planet
      return { mesh, kind: 'scatter' };
    } catch { /* fall back to O'Neil */ }
  }
  if (o.haze) return { mesh: hazeShell(o as Parameters<typeof hazeShell>[0]), kind: 'haze' };
  const tint = new THREE.Color().setHSL(o.hue, 0.55, 0.6);
  // Wavelengths from the tint: a bluer planet scatters shorter light harder.
  // natural objects: a planet kind's own shell — its colour (as wavelengths: the shell scatters as λ⁻⁴), reach and strength;
  // every option absent = the stock shell (1.025 R, the hue's tint, eSun 18)
  const wl: [number, number, number] = o.wavelengths ?? [0.65 + (1 - tint.r) * 0.06, 0.57 + (1 - tint.g) * 0.05, 0.475 + (1 - tint.b) * 0.05];
  const outerK = o.outer ?? 1.025;
  const { material, uniforms } = createPlanetAtmosphereMaterial({ innerRadius: o.radius, outerRadius: o.radius * outerK, sunDirection: o.sunDir, wavelengths: wl, samples: 5, eSun: o.eSun ?? 18, intensity: o.intensity ?? 1, ...(o.km !== undefined ? { km: o.km } : {}) });
  if (o.set) wrapShellVisibility(material, o.set, o.radius); // shader gaps R4: ONE visibility on the atmosphere's sun term (at the limb point)
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(o.radius * outerK, 64, 48), seatBodyLayer(markMrtOverlay(material), 'behind')); // sky/post (2026-09-27): the MRT overlay law
  mesh.name = 'planet-atmosphere';
  mesh.renderOrder = 2;
  uniforms.sunDirection.value.copy(o.sunDir); // the SAME direction the key light, the disc and the planet use (one sun)
  return { mesh, kind: 'oneil' };
}

/**
 * natural objects — AN EXTENDED HAZE (a super-puff, a hazy moon, a greenhouse deck's top). O'Neil's shell is a fit for
 * ONE thickness (1.025 R, scale depth 0.25): stretched to 1.1 R it drew a bright ring at the mesh's edge with a dark
 * gap inside (the first bed pictures). This is the closed form instead: through an exponential atmosphere of scale
 * height H a ray whose closest approach is b sees the column N(b) = N₀·e^(−(b−R)/H)·√(b/R), so the limb is
 * 1 − e^(−τ(b)) of the haze's colour, lit where its limb point sees the star (a soft terminator), brighter looking
 * toward the star (forward scattering) and faded to nothing at the mesh's edge. Same seat and order as the stock shell.
 */
function hazeShell(o: { radius: number; sunDir: THREE.Vector3; set?: EclipseSet | null } & ShellExtra & { haze: NonNullable<ShellExtra['haze']> }): THREE.Mesh {
  const hz = o.haze, R = o.radius, outerK = o.outer ?? 1.06, H = Math.max(1e-4, hz.scale) * R;
  const uSunW = uniform(o.sunDir.clone().normalize());
  const c = new THREE.Color(hz.color), cO = new THREE.Color(hz.outer ?? hz.color);
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.BackSide, blending: THREE.AdditiveBlending, fog: false });
  (mat as unknown as { forceSinglePass: boolean }).forceSinglePass = true;
  mat.colorNode = Fn(() => {
    const fr = planetFrame();
    const dir = normalize(positionWorld.sub(cameraPosition)).toVar();
    const tca = dot(fr.centre.sub(cameraPosition), dir).toVar();
    const Q = cameraPosition.add(dir.mul(tca)).sub(fr.centre).div(fr.k).toVar();   // the limb point, true metres round the centre
    const b = max(length(Q), float(R * 1e-3)).toVar();
    const tau = exp(max(b.sub(R), float(0)).div(-H)).mul(sqrt(b.div(R))).mul(hz.tau);
    const edge = float(1).sub(smoothstep(R * (1 + (outerK - 1) * 0.7), R * outerK, b));  // nothing at the mesh's silhouette
    const lit = smoothstep(-0.3, 0.3, dot(Q.div(b), uSunW));                              // the limb point's own daylight
    const g = 0.6, cosT = dot(dir, uSunW);
    const hg = float(1 - g * g).div(pow(max(float(1 + g * g).sub(cosT.mul(2 * g)), float(1e-4)), float(1.5)));
    const phase = hg.min(6).mul(0.3).add(0.7);
    const a = float(1).sub(exp(tau.negate()));
    // (a second, thinner colour above the main deck: Titan's detached haze is blue over its orange)
    const col = mix(vec3(cO.r, cO.g, cO.b), vec3(c.r, c.g, c.b), smoothstep(0.08, 0.5, a));
    return vec4(col.mul(vec3(uPlanetSun)).mul(a.mul(lit).mul(phase).mul(edge).mul(step(0, tca)).mul(hz.gain)), 1);
  })();
  mat.name = 'planet-haze-shell';
  if (o.set) wrapShellVisibility(mat, o.set, R);
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(R * outerK, 64, 48), seatBodyLayer(markMrtOverlay(mat), 'behind'));
  mesh.name = 'planet-atmosphere-haze';
  mesh.renderOrder = 2;
  mesh.userData.sunDir = uSunW;   // (a moon's sun moves: its owner writes the direction here)
  return mesh;
}

/**
 * The Commons' night: every settlement site as a warm Gaussian cluster in a small equirect (capitals and cities
 * bright and wide, towns smaller, hamlets a pinprick). Sites are placed on a mid-latitude band of the sphere by
 * their map coordinates (the ±1,000 km shard). Built once per seed (`userData.shared`).
 */
let cityTex: { seed: string; tex: THREE.DataTexture } | null = null;
export function cityLightsTexture(seed: string, sites: ReadonlyArray<{ centerX: number; centerZ: number; tier: string }>): THREE.DataTexture {
  if (cityTex && cityTex.seed === seed) return cityTex.tex;
  const W = 1024, Hh = 512;
  const data = new Float32Array(W * Hh);
  for (const s of sites) {
    const u = 0.5 + (s.centerX / CITY_MAP.span) * CITY_MAP.ku; // ONE mapping for the lights and the orbital clouds (orbital_clouds.CITY_MAP)
    const v = 0.5 + (s.centerZ / CITY_MAP.span) * CITY_MAP.kv;
    const big = s.tier === 'capital' || s.tier === 'city';
    const rad = big ? 3.2 : s.tier === 'town' || s.tier === 'district' ? 1.8 : 1.0;
    const amp = big ? 1.0 : s.tier === 'town' || s.tier === 'district' ? 0.6 : 0.35;
    const cx = u * W, cy = v * Hh;
    const R = Math.ceil(rad * 3);
    for (let y = Math.max(0, Math.floor(cy - R)); y < Math.min(Hh, Math.ceil(cy + R)); y++) {
      for (let x = Math.floor(cx - R); x < Math.ceil(cx + R); x++) {
        const xx = ((x % W) + W) % W; // wrap in longitude
        const d2 = (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2;
        data[y * W + xx] += amp * Math.exp(-d2 / (2 * rad * rad));
      }
    }
  }
  // 8-bit (the shader scales ×1.5): a float texture would need OES_texture_float_linear to filter on WebGL2.
  const rgba = new Uint8Array(W * Hh * 4);
  let lit = 0;
  for (let i = 0; i < W * Hh; i++) { const v = Math.min(1.5, data[i]); rgba[i * 4] = Math.round((v / 1.5) * 255); rgba[i * 4 + 3] = 255; if (v > 0.05) lit++; }
  const tex = new THREE.DataTexture(rgba, W, Hh, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.wrapS = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.generateMipmaps = false;
  tex.needsUpdate = true;
  tex.userData.shared = true;
  cityTex = { seed, tex };
  planetLookState.cityTexels = lit;
  return tex;
}
